import { useCallback, useRef, useState } from "react";
import dayjs from "dayjs";

/**
 * Time ranges of the Measurements tab (tri-server #1434). Field devices such as FireIQ
 * extinguisher monitors transmit about every 14 days, so a "Today" default shows empty charts
 * although the device works. The tab therefore opens on the last 30 days and widens on its own
 * when even that holds no data.
 */
export type MeasurementRangeKey = "last30d" | "last90d" | "last365d" | "all" | "userDefined";

export const DEFAULT_RANGE: MeasurementRangeKey = "last30d";

/** The preset ranges in the order the tab widens through them. */
export const WIDENING_ORDER: MeasurementRangeKey[] = ["last30d", "last90d", "last365d", "all"];

/** Option order of the dropdown; labels live under `assetPropertiesPanel.range.<key>`. */
export const RANGE_OPTION_KEYS: MeasurementRangeKey[] = [...WIDENING_ORDER, "userDefined"];

const RANGE_DAYS: Partial<Record<MeasurementRangeKey, number>> = {
  last30d: 30,
  last90d: 90,
  last365d: 365,
};

/** The window of a preset range ending today; `all` reaches back to the epoch. */
export const rangeWindow = (
  key: MeasurementRangeKey,
  now: Date = new Date(),
): { start: Date; end: Date } => {
  const today = dayjs(now);
  const days = RANGE_DAYS[key];
  const start = days === undefined ? new Date(0) : today.subtract(days, "d").startOf("d").toDate();
  return { start, end: today.endOf("d").toDate() };
};

/** The next wider preset, or undefined if there is none (`all`, `userDefined`). */
export const nextWiderRange = (key: MeasurementRangeKey): MeasurementRangeKey | undefined => {
  const index = WIDENING_ORDER.indexOf(key);
  return index >= 0 ? WIDENING_ORDER[index + 1] : undefined;
};

/**
 * State of the range picker. A manual choice (dropdown or date fields) is final for the rest of
 * the page visit: `reportData(false)` widens only while the user has not chosen themselves.
 */
export const useMeasurementRange = () => {
  const [range, setRange] = useState<MeasurementRangeKey>(DEFAULT_RANGE);
  const [start, setStart] = useState(() => rangeWindow(DEFAULT_RANGE).start);
  const [end, setEnd] = useState(() => rangeWindow(DEFAULT_RANGE).end);
  const [autoWidenedTo, setAutoWidenedTo] = useState<MeasurementRangeKey | null>(null);
  const userChose = useRef(false);

  const apply = useCallback((key: MeasurementRangeKey) => {
    setRange(key);
    if (key !== "userDefined") {
      const window = rangeWindow(key);
      setStart(window.start);
      setEnd(window.end);
    }
  }, []);

  /** The user picked a range in the dropdown. */
  const select = useCallback(
    (key: MeasurementRangeKey) => {
      userChose.current = true;
      setAutoWidenedTo(null);
      apply(key);
    },
    [apply],
  );

  /** The user typed a date into one of the date fields (only enabled for `userDefined`). */
  const setCustomStart = useCallback((date: Date) => {
    userChose.current = true;
    setStart(date);
  }, []);
  const setCustomEnd = useCallback((date: Date) => {
    userChose.current = true;
    setEnd(date);
  }, []);

  /**
   * Called after each load. Without any data in an automatically chosen range, switches to the
   * next wider one. Returns true if it did, so the caller can skip drawing the empty result.
   */
  const reportData = useCallback(
    (hasData: boolean): boolean => {
      if (hasData || userChose.current) return false;
      const wider = nextWiderRange(range);
      if (!wider) return false;
      setAutoWidenedTo(wider);
      apply(wider);
      return true;
    },
    [apply, range],
  );

  /** Back to the default for a different asset. */
  const reset = useCallback(() => {
    userChose.current = false;
    setAutoWidenedTo(null);
    apply(DEFAULT_RANGE);
  }, [apply]);

  return {
    range,
    start,
    end,
    autoWidenedTo,
    select,
    setCustomStart,
    setCustomEnd,
    reportData,
    reset,
  };
};
